import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  createContext,
  useContext,
  type FC,
} from "react";
import {
  ActionBarPrimitive,
  AuiIf,
  ErrorPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAuiState,
} from "@assistant-ui/react";
import { AlertCircle, CheckIcon, CopyIcon } from "lucide-react";
import { useAllMcpAppEntries } from "~/components/assistant-v2/AgentContext";
import { useAgentEvents } from "~/components/assistant-v2/AgentEventProvider";
import {
  ChainOfThought,
  ToolSectionProvider,
  UnifiedToolHeader,
} from "~/components/assistant-v2/ChainOfThought";
import { Composer, type ComposerProps } from "~/components/assistant-v2/Composer";
import { DefaultToolMessage } from "~/components/assistant-v2/DefaultToolMessage";
import { MarkdownText, StandaloneMarkdown } from "~/components/assistant-v2/MarkdownText";
import { McpAppFrame } from "~/components/assistant-v2/McpAppFrame";
import {
  MessageFeedbackInput,
  SubmittedFeedbackDisplay,
} from "~/components/assistant-v2/MessageFeedback";
import { TooltipIconButton } from "~/components/assistant-v2/TooltipIconButton";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { ShimmerText } from "~/components/assistant/ShimmerText";
import { ZENDESK_AI_ASSISTANT_HELP_URL } from "~/components/assistant/constants";
import { CancellationMessage } from "~/components/assistant/messages/CancellationMessage";
import type { AssistantMessageCustomMetadata, FeedbackCreate } from "~/components/assistant/types";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { TextOverflow } from "~/components/ui/text-overflow";
import { cn } from "~/lib/utils";

/**
 * Context providing a Map<messageId, turnId> for all streaming turns in
 * the current session. Historical turns get their turn IDs from message
 * metadata; this map covers messages created during streaming.
 */
const StreamingTurnIdsContext = createContext<Map<string, string>>(new Map());

/** Extract typed custom metadata from an assistant-ui message. */
function getCustomMetadata(message: {
  metadata?: unknown;
}): AssistantMessageCustomMetadata | undefined {
  return (message.metadata as { custom?: AssistantMessageCustomMetadata } | undefined)?.custom;
}

/**
 * Context flag signaling a read-only thread (shared-conversation viewer).
 * Consumed by AssistantActionBar to suppress the copy + feedback controls,
 * and by any child that mutates state.
 */
const ReadOnlyContext = createContext<boolean>(false);

export interface ThreadProps extends ComposerProps {
  suggestions?: Array<{ title: string; prompt: string }>;
  /** When true, hides the composer and action bar (shared-conversation viewer). */
  readOnly?: boolean;
}

// @lat: [[frontend/ai-assistant#Dataset Assistant]]
export const Thread: FC<ThreadProps> = ({ suggestions, readOnly = false, ...composerProps }) => {
  const {
    streamingTurnIds,
    runError,
    contextBuffer,
    isLoadingHistory,
    historyHasMessages,
    isRunningRemotely,
    isCancelling,
  } = useAgentEvents();

  return (
    <ReadOnlyContext.Provider value={readOnly}>
      <StreamingTurnIdsContext.Provider value={streamingTurnIds}>
        <ThreadPrimitive.Root
          className="aui-root aui-thread-root @container flex h-full flex-col bg-background"
          style={{
            ["--thread-max-width" as string]: "56rem",
          }}
        >
          <ThreadPrimitive.Viewport
            turnAnchor={readOnly ? "bottom" : "top"}
            autoScroll={!readOnly}
            className={`aui-thread-viewport relative flex flex-1 flex-col overflow-x-auto overflow-y-auto px-4 pt-4 scrollbar-subtle ${readOnly ? "pb-4" : ""}`}
          >
            <ThreadEmptyState
              isLoadingHistory={isLoadingHistory}
              historyHasMessages={historyHasMessages}
              suggestions={suggestions}
              readOnly={readOnly}
            />

            <ThreadPrimitive.Messages
              components={{
                UserMessage,
                AssistantMessage,
              }}
            />

            {isRunningRemotely && <RemoteRunIndicator />}

            {!readOnly && (
              <ThreadPrimitive.ViewportFooter className="aui-thread-viewport-footer sticky bottom-0 mx-auto mt-auto flex w-full max-w-(--thread-max-width) flex-col gap-2 overflow-visible rounded-t-3xl bg-background">
                {runError && <RunError message={runError} />}
                <div className="relative mx-auto w-full max-w-(--thread-max-width)">
                  <div className="relative z-20">
                    <Composer {...composerProps} contextBuffer={contextBuffer} />
                  </div>
                  <CancellingBanner isVisible={isCancelling} />
                </div>
                <div className="text-center text-xs text-gray-500 pb-2">
                  AI can make mistakes. Review for accuracy.
                </div>
              </ThreadPrimitive.ViewportFooter>
            )}
          </ThreadPrimitive.Viewport>
        </ThreadPrimitive.Root>
      </StreamingTurnIdsContext.Provider>
    </ReadOnlyContext.Provider>
  );
};

const MessageSkeletons: FC = () => {
  return (
    <div className="mx-auto w-full max-w-(--thread-max-width) flex flex-col gap-6 py-4 animate-pulse">
      {[0, 1, 2].map((turn) => (
        <div key={turn} className="flex flex-col gap-4">
          <div className="flex justify-end px-2">
            <div className="h-10 w-2/5 rounded-2xl bg-muted" />
          </div>
          <div className="flex flex-col gap-2 px-2">
            <div className="h-4 w-3/4 rounded bg-muted" />
            <div className="h-4 w-1/2 rounded bg-muted" />
            <div className="h-4 w-5/6 rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
};

const SuggestionButton: FC<{
  ariaLabel: string;
  title: string;
  onClick?: () => void;
}> = ({ ariaLabel, title, onClick }) => (
  <Button
    variant="ghost"
    onClick={onClick}
    className="aui-thread-welcome-suggestion h-auto w-full items-start justify-start overflow-hidden rounded-xl border px-3 py-2 text-left text-sm transition-colors hover:bg-muted"
    aria-label={ariaLabel}
  >
    <TextOverflow
      autoTooltip
      delayDuration={700}
      className="aui-thread-welcome-suggestion-text-1 block w-full font-medium"
    >
      {title}
    </TextOverflow>
  </Button>
);

/**
 * Shows loading skeletons while history is being fetched, or the welcome
 * screen when the thread is genuinely empty.
 *
 * Uses `historyHasMessages` (a deterministic React-state signal from the
 * persistence hook) instead of `thread.isEmpty` (an external store value
 * that settles asynchronously after `runtime.thread.reset()`). This
 * eliminates the one-frame flicker where the welcome screen appears
 * between "loading done" and "messages populated in the external store".
 */
const ThreadEmptyState: FC<{
  isLoadingHistory?: boolean;
  historyHasMessages?: boolean;
  suggestions?: Array<{ title: string; prompt: string }>;
  readOnly?: boolean;
}> = ({ isLoadingHistory, historyHasMessages, suggestions, readOnly }) => {
  if (isLoadingHistory) {
    return <MessageSkeletons />;
  }

  if (historyHasMessages) {
    return null;
  }

  if (readOnly) {
    return (
      <AuiIf condition={({ thread }) => thread.isEmpty}>
        <ReadOnlyEmptyState />
      </AuiIf>
    );
  }

  return (
    <AuiIf condition={({ thread }) => thread.isEmpty}>
      <ThreadWelcome suggestions={suggestions} />
    </AuiIf>
  );
};

const ReadOnlyEmptyState: FC = () => (
  <div className="aui-thread-empty-readonly mx-auto my-auto flex w-full max-w-(--thread-max-width) grow flex-col items-center justify-center px-4 text-center">
    <h1 className="text-lg font-semibold">No messages</h1>
    <p className="mt-1 text-sm text-gray-600">This conversation is empty.</p>
  </div>
);

interface ThreadWelcomeProps {
  suggestions?: Array<{ title: string; prompt: string }>;
}

const ThreadWelcome: FC<ThreadWelcomeProps> = ({ suggestions }) => {
  return (
    <div className="aui-thread-welcome-root mx-auto my-auto flex w-full max-w-(--thread-max-width) grow flex-col">
      <div className="flex w-full flex-1 flex-col justify-center px-4">
        <Badge className="mb-3">Beta</Badge>
        <h1 className="text-xl font-semibold leading-tight">Analysis Agent</h1>
        <p className="my-2 text-sm leading-relaxed text-gray-600">
          Ask questions, generate tables or charts, and compare data across tables.{" "}
          <a
            href={ZENDESK_AI_ASSISTANT_HELP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline cursor-pointer"
          >
            View Usage Guide
          </a>
        </p>
      </div>
      <ThreadSuggestions suggestions={suggestions} />
    </div>
  );
};

const ThreadSuggestions: FC<{
  suggestions?: Array<{ title: string; prompt: string }>;
}> = ({ suggestions }) => {
  return (
    <div className="aui-thread-welcome-suggestions grid w-full gap-2 pb-4 @md:grid-cols-2">
      {suggestions?.map((suggestion, index) => (
        <div
          key={suggestion.prompt}
          className="aui-thread-welcome-suggestion-display min-w-0 fade-in slide-in-from-bottom-2 animate-in fill-mode-both duration-200"
          style={{ animationDelay: `${100 + index * 50}ms` }}
        >
          <ThreadPrimitive.Suggestion prompt={suggestion.prompt} asChild>
            <SuggestionButton ariaLabel={suggestion.prompt} title={suggestion.title} />
          </ThreadPrimitive.Suggestion>
        </div>
      ))}
    </div>
  );
};

const MessageError: FC = () => {
  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="aui-message-error-root mt-2 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive text-sm dark:bg-destructive/5 dark:text-red-200">
        <ErrorPrimitive.Message className="aui-message-error-message line-clamp-2" />
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

const CancellingBanner: FC<{ isVisible: boolean }> = ({ isVisible }) => {
  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-x-4 bottom-[calc(100%)] z-10",
        "transform-gpu transition-all duration-200 ease-out",
        isVisible ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
      )}
      aria-hidden={!isVisible}
    >
      <div className="mx-auto flex w-fit items-center justify-center gap-2 rounded-t-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-500 shadow-sm">
        <div className="size-3 animate-spin rounded-full border-1 border-current border-t-transparent" />
        Cancelling...
      </div>
    </div>
  );
};

const RunError: FC<{ message: string }> = ({ message }) => {
  return (
    <div className="mx-auto w-full max-w-(--thread-max-width) px-2 py-3">
      <div className="flex items-start gap-3 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive text-sm dark:bg-destructive/5 dark:text-red-200">
        <AlertCircle className="mt-0.5 size-4 shrink-0" />
        <span>{message}</span>
      </div>
    </div>
  );
};

/**
 * Animated ping dot with optional shimmer label.
 *
 * - No text: renders just the dot (use inline after streaming text).
 * - With text: renders dot + shimmer label (use as a block indicator).
 */
const ThinkingIndicator: FC<{
  text?: string;
  className?: string;
  showPulse?: boolean;
}> = ({ text = "Thinking", className, showPulse = false }) => (
  <span className={cn("inline-flex items-center gap-1.5 text-sm text-gray-500", className)}>
    {showPulse && (
      <span className="relative inline-flex size-2 shrink-0">
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-gray-400 opacity-75" />
        <span className="relative inline-flex size-2 rounded-full bg-gray-400" />
      </span>
    )}
    {text && <ShimmerText className="text-sm font-medium">{text}</ShimmerText>}
  </span>
);

/**
 * Thread-level "Thinking" indicator for remotely-running conversations.
 *
 * Only renders when the runtime does NOT have a local run active. If a
 * local SSE stream is running, the in-message UnifiedToolHeader already
 * shows "Thinking" — this avoids a duplicate indicator.
 */
const RemoteRunIndicator: FC = () => {
  const isLocallyRunning = useAuiState((s) => s.thread.isRunning);
  if (isLocallyRunning) return null;
  return (
    <div className="mx-auto w-full max-w-(--thread-max-width) py-4 px-2">
      <ThinkingIndicator />
    </div>
  );
};

/**
 * Suppress inline text parts — the last text content is rendered by
 * {@link LastTextContent} below the parts loop so it always appears
 * after all tool-call cards.
 */
const AssistantText: FC = () => null;

/**
 * Renders the last text part of the assistant message below all other
 * parts (tool calls, chain-of-thought, etc.). The backend emits multiple
 * TextMessage events during a turn — intermediate narration between tool
 * calls, then the final response. Only the last one is shown, and it
 * always appears at the bottom of the message.
 *
 * Uses {@link StandaloneMarkdown} to render directly from message state,
 * independent of the part context lifecycle (avoids disappearing when the
 * message transitions from running → complete).
 */
const LastTextContent: FC = () => {
  const lastText = useAuiState((s) => {
    const parts = s.message.parts;
    if (!parts) return null;
    for (let i = parts.length - 1; i >= 0; i--) {
      const part = parts[i];
      if (part.type === "text") {
        return (part as { text: string }).text;
      }
    }
    return null;
  });

  if (!lastText) return null;

  return <StandaloneMarkdown text={lastText} />;
};

/**
 * Renders MCP app frames for any tool calls in this message that have one,
 * or falls back to the last text content when none are present.
 */
const TurnBottomContent: FC = () => {
  const mcpAppMap = useAllMcpAppEntries();
  const parts = useAuiState((s) => s.message.parts ?? []);

  const mcpEntries = useMemo(() => {
    return parts
      .filter((p) => p.type === "tool-call")
      .map((p) => mcpAppMap.get((p as unknown as { toolCallId: string }).toolCallId))
      .filter((entry): entry is NonNullable<typeof entry> => entry != null);
  }, [parts, mcpAppMap]);

  // Intentional: when MCP app frames are present, they fully replace LastTextContent.
  // Any follow-up text the agent emits after the tool call is not rendered — the
  // app frame is the canonical response surface for MCP tool turns.
  if (mcpEntries.length > 0) {
    return (
      <div className="-mx-3">
        {mcpEntries.map((entry, i) =>
          entry.html === null ? (
            <p key={i} className="text-xs text-red-600 mt-2 mx-2">
              Unable to load tool preview
            </p>
          ) : (
            <McpAppFrame
              key={i}
              html={entry.html}
              title={entry.title}
              toolResult={entry.toolResult}
            />
          )
        )}
      </div>
    );
  }

  return <LastTextContent />;
};

/**
 * Shows a "Thinking" indicator when the message is running but no content
 * parts have arrived yet (e.g. right after submitting a prompt, or the
 * brief gap between agent cycles). Once any part appears — reasoning,
 * tool-call, or text — the CoT and Text components take over.
 */
const MessageRunningEmpty: FC = () => {
  const isRunning = useAuiState((s) => s.message.status?.type === "running");
  const hasContent = useAuiState((s) => (s.message.content?.length ?? 0) > 0);

  if (!isRunning || hasContent) return null;
  return <ThinkingIndicator />;
};

const AssistantMessage: FC = () => {
  const readOnly = useContext(ReadOnlyContext);
  const message = useAuiState((s) => s.message);
  const custom = getCustomMetadata(message);
  const showCancelled =
    custom?.cancelled === true ||
    (message.status?.type === "incomplete" && message.status?.reason === "cancelled");

  // In the shared-conversation viewer, surface submitted feedback with a
  // colored border + inline note so reviewers can spot it at a glance.
  const submittedFeedback = readOnly ? (custom?.feedback ?? null) : null;
  const isPositive = submittedFeedback?.feedback_value === 1;

  return (
    <MessagePrimitive.Root
      className={cn(
        "aui-assistant-message-root relative mx-auto w-full max-w-(--thread-max-width) py-3",
        !readOnly && "fade-in slide-in-from-bottom-1 animate-in duration-150",
        submittedFeedback &&
          cn("rounded-xl border-[6px] p-2", isPositive ? "border-green-100" : "border-red-100")
      )}
      data-role="assistant"
      // Stable selector for smoke + integration tests. Don't remove. Don't rename.
      data-testid="assistant-message"
    >
      {submittedFeedback && (
        <div className="px-2 pb-2">
          <SubmittedFeedbackDisplay feedback={submittedFeedback} turnId={custom?.turnId} />
        </div>
      )}
      <div className="aui-assistant-message-content wrap-break-word px-2 text-foreground leading-relaxed text-[14px]">
        {showCancelled ? (
          <CancellationMessage />
        ) : (
          <ToolSectionProvider>
            <UnifiedToolHeader />

            <MessagePrimitive.Parts
              components={{
                Text: AssistantText,
                ChainOfThought,
              }}
            />

            <TurnBottomContent />
            <MessageRunningEmpty />
            <MessageError />
          </ToolSectionProvider>
        )}
      </div>

      {!readOnly && (
        <div className="aui-assistant-message-footer mt-1 ml-2 flex">
          <AssistantActionBar />
        </div>
      )}
    </MessagePrimitive.Root>
  );
};

/**
 * Resolves the turn ID and existing feedback for the turn that this
 * assistant message belongs to.
 *
 * For history-loaded messages, the turn ID lives in metadata.custom.
 * For streamed messages, it's looked up from the streaming turn IDs map
 * (populated when RunStartedEvent fires during the SSE stream).
 */
function useTurnFeedbackInfo() {
  const streamingTurnIds = useContext(StreamingTurnIdsContext);
  const message = useAuiState((s) => s.message);

  // History-loaded messages carry the turn ID in metadata.custom
  const custom = getCustomMetadata(message);
  if (custom?.turnId) {
    return { turnId: custom.turnId, feedback: custom.feedback ?? null };
  }

  // Streamed messages: look up by message ID
  const turnId = streamingTurnIds.get(message.id);
  if (turnId) {
    return { turnId, feedback: null };
  }

  return null;
}

const AssistantActionBar: FC = () => {
  const { api, conversationId } = useAssistantContext();
  const readOnly = useContext(ReadOnlyContext);
  const message = useAuiState((s) => s.message);
  const info = useTurnFeedbackInfo();

  // Hide the action bar while this specific message is still being generated
  const isRunning = message.status?.type === "running";

  const [feedback, setFeedback] = useState<FeedbackCreate | null>(info?.feedback ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync with history-loaded feedback when info changes (e.g. conversation switch)
  useEffect(() => {
    setFeedback(info?.feedback ?? null);
  }, [info?.feedback]);

  const handleSubmit = useCallback(
    async (value: 0 | 1, text?: string | null, impact?: number | null) => {
      if (!conversationId || !info?.turnId || isSubmitting) return;
      setIsSubmitting(true);
      try {
        const submitted: FeedbackCreate = {
          feedback_type: "BINARY" as const,
          feedback_value: value,
          text: text ?? null,
          impact: impact ?? null,
        };
        await api.putTurnFeedback(conversationId, info.turnId, submitted);
        setFeedback(submitted);
      } catch (error) {
        console.error("Failed to submit feedback:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [api, conversationId, info?.turnId, isSubmitting]
  );

  const handleClear = useCallback(async () => {
    if (!conversationId || !info?.turnId || isSubmitting || !api.deleteTurnFeedback) return;
    setIsSubmitting(true);
    try {
      await api.deleteTurnFeedback(conversationId, info.turnId);
      setFeedback(null);
    } catch (error) {
      console.error("Failed to clear feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [api, conversationId, info?.turnId, isSubmitting]);

  if (isRunning || readOnly) return null;

  return (
    <ActionBarPrimitive.Root
      autohide="never"
      className="aui-assistant-action-bar-root col-start-3 row-start-2 -ml-1 flex gap-1 text-muted-foreground"
    >
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip="Copy">
          <AuiIf condition={({ message }) => message.isCopied}>
            <CheckIcon />
          </AuiIf>
          <AuiIf condition={({ message }) => !message.isCopied}>
            <CopyIcon />
          </AuiIf>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      {info?.turnId && (
        <MessageFeedbackInput
          feedback={feedback}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />
      )}
    </ActionBarPrimitive.Root>
  );
};

/** ~3 lines at 14px font / relaxed leading */
const USER_MSG_COLLAPSED_HEIGHT = 70;

const UserMessage: FC = () => {
  const readOnly = useContext(ReadOnlyContext);
  const message = useAuiState((s) => s.message);
  const turnId = getCustomMetadata(message)?.turnId;

  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }
    const check = () => setIsOverflowing(el.scrollHeight > USER_MSG_COLLAPSED_HEIGHT);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const collapse = useCallback(() => {
    setIsExpanded(false);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    if (!isExpanded) {
      return;
    }
    function handleClick(e: MouseEvent) {
      if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
        collapse();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isExpanded, collapse]);

  const expand = useCallback(() => {
    if (isOverflowing && !isExpanded) {
      setIsExpanded(true);
    }
  }, [isOverflowing, isExpanded]);

  return (
    <MessagePrimitive.Root
      className={cn(
        "aui-user-message-root relative mx-auto grid w-full max-w-(--thread-max-width) auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-2 px-2 py-3 [&:where(>*)]:col-start-2",
        !readOnly && "fade-in slide-in-from-bottom-1 animate-in duration-150"
      )}
      data-role="user"
    >
      {/* Scroll target for ?turnId= deep-links and feedback navigation.
          Offset so the message isn't flush against the viewport top. */}
      {turnId && (
        <div id={`turn-${turnId}`} aria-hidden className="pointer-events-none absolute -top-16" />
      )}
      <div className="aui-user-message-content-wrapper relative col-start-2 min-w-0">
        <div
          ref={contentRef}
          role={isOverflowing ? "button" : undefined}
          tabIndex={isOverflowing ? 0 : undefined}
          onClick={expand}
          onKeyDown={
            isOverflowing && !isExpanded ? (e) => e.key === "Enter" && expand() : undefined
          }
          className={cn(
            "aui-user-message-content wrap-break-word rounded-2xl bg-muted px-4 py-2.5 text-foreground text-[14px] relative grid w-full items-start grid-cols-[1fr]",
            isOverflowing && !isExpanded && "cursor-pointer"
          )}
          style={{
            maxHeight: isExpanded ? "20vh" : `${USER_MSG_COLLAPSED_HEIGHT}px`,
            overflowY: isExpanded ? "auto" : "hidden",
          }}
        >
          <MessagePrimitive.Parts />
          {isOverflowing && !isExpanded && (
            <div
              className="absolute bottom-0 left-0 right-0 h-8 rounded-b-2xl pointer-events-none bg-muted"
              style={{
                maskImage: "linear-gradient(to bottom, transparent, black)",
                WebkitMaskImage: "linear-gradient(to bottom, transparent, black)",
              }}
            />
          )}
        </div>
      </div>
    </MessagePrimitive.Root>
  );
};
